import type { GradingTreePayload } from '../../Workspace/admin/grading/types';
import type { AuthorProfile } from '../types';
import {
  COMMUNITY_DEV_ADMIN_EMAIL,
  getMarketProfileDevPreset,
  setMarketProfileDevPresetStorage,
  type MarketProfileDevPreset,
} from '../communityDevMode';
import {
  computeTotalComp,
  emptyPositionForm,
  formatRub,
  normalizePositionFormData,
  parseMoney,
  positionFixValue,
  positionFixMonthlyValue,
  positionHasLtip,
  positionLtipAmountValue,
  positionStiValue,
  todayIsoDate,
  finalizePositionFormForSubmit,
  POSITION_QUESTIONNAIRE_SOURCE,
  type PositionFormData,
} from './positionFormModel';

export {
  computeTotalComp,
  emptyPositionForm,
  finalizePositionFormForSubmit,
  formatRub,
  normalizePositionFormData,
  parseMoney,
  positionFixValue,
  positionFixMonthlyValue,
  positionHasLtip,
  positionLtipAmountValue,
  positionStiValue,
  todayIsoDate,
  POSITION_QUESTIONNAIRE_SOURCE,
  type PositionFormData,
};

export const MARKET_PROFILE_STORAGE_KEY = 'dc_market_profile_v1';

/** Через сколько дней данные считаются устаревшими */
const OUTDATED_AFTER_DAYS = 180;

export type QuestionnaireStatus = 'empty' | 'draft' | 'pending_review' | 'complete';

/** Срок проверки анкеты после отправки */
export const QUESTIONNAIRE_REVIEW_MS = 24 * 60 * 60 * 1000;

/** Публичный профиль участника сообщества */
export type UserProfileFormData = {
  lastName: string;
  firstName: string;
  pseudonym: string;
  email: string;
  sectorName: string;
  companyName: string;
  positionName: string;
  phone: string;
};

/** Настройки видимости данных в сообществе */
export type CommunityVisibilitySettings = {
  /** Показывать только псевдоним, скрывая остальное */
  pseudonymOnly: boolean;
  showPosition: boolean;
  showSector: boolean;
  showCompany: boolean;
};

export type UserProfileState = {
  data: UserProfileFormData;
  visibility: CommunityVisibilitySettings;
  updatedAt: string | null;
};

/** Поля анкеты «Данные о должности» — см. positionFormModel.ts */

export type PositionState = {
  status: QuestionnaireStatus;
  data: PositionFormData;
  updatedAt: string | null;
  reviewSubmittedAt: string | null;
  approvedAt?: string | null;
};

export type GradingAnswerSnapshot = {
  questionId: number;
  factorName: string;
  questionText: string;
  answerText: string;
  answerScore: number;
};

export type GradingResultSnapshot = {
  familyName: string;
  specializationName: string | null;
  roleClusterName: string;
  totalScore: number;
  roleName: string | null;
  gradeLevel: number | null;
};

export type GradingState = {
  status: QuestionnaireStatus;
  result: GradingResultSnapshot | null;
  answers: GradingAnswerSnapshot[];
  updatedAt: string | null;
  reviewSubmittedAt: string | null;
  approvedAt?: string | null;
};

export type MarketProfileState = {
  userProfile: UserProfileState;
  position: PositionState;
  grading: GradingState;
  confirmedAt: string | null;
};

export function emptyUserProfileForm(): UserProfileFormData {
  return {
    lastName: '',
    firstName: '',
    pseudonym: '',
    email: '',
    sectorName: '',
    companyName: '',
    positionName: '',
    phone: '',
  };
}

export function defaultCommunityVisibility(): CommunityVisibilitySettings {
  return {
    pseudonymOnly: false,
    showPosition: true,
    showSector: true,
    showCompany: false,
  };
}

export function emptyUserProfile(): UserProfileState {
  return {
    data: emptyUserProfileForm(),
    visibility: defaultCommunityVisibility(),
    updatedAt: null,
  };
}

export function emptyMarketProfile(): MarketProfileState {
  return {
    userProfile: emptyUserProfile(),
    position: { status: 'empty', data: emptyPositionForm(), updatedAt: null, reviewSubmittedAt: null },
    grading: { status: 'empty', result: null, answers: [], updatedAt: null, reviewSubmittedAt: null },
    confirmedAt: null,
  };
}

export function isQuestionnaireSubmitted(status: QuestionnaireStatus): boolean {
  return status === 'pending_review' || status === 'complete';
}

/** Анкета уже отправлена — повторное заполнение недоступно. */
export function isQuestionnaireLocked(status: QuestionnaireStatus): boolean {
  return isQuestionnaireSubmitted(status);
}

export function reviewTimeRemaining(reviewSubmittedAt: string | null): number | null {
  if (!reviewSubmittedAt) return null;
  const started = new Date(reviewSubmittedAt).getTime();
  if (Number.isNaN(started)) return null;
  return Math.max(0, QUESTIONNAIRE_REVIEW_MS - (Date.now() - started));
}

export function isReviewPeriodOver(reviewSubmittedAt: string | null): boolean {
  const remaining = reviewTimeRemaining(reviewSubmittedAt);
  return remaining !== null && remaining <= 0;
}

export function formatReviewCountdown(ms: number): string {
  const totalSec = Math.ceil(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${h} ч ${String(m).padStart(2, '0')} мин ${String(s).padStart(2, '0')} сек`;
}

/** Раньше переводило pending_review → complete по таймеру; теперь только администратор. */
export function applyReviewTimeouts(state: MarketProfileState): MarketProfileState {
  return state;
}

export function hasMarketDataAccess(state: MarketProfileState): boolean {
  return state.position.status === 'complete' && state.grading.status === 'complete';
}

export function normalizeProfileEmail(email: string | null | undefined): string {
  return (email ?? '').trim().toLowerCase();
}

export function marketProfileStorageKey(userEmail?: string | null): string {
  const email = normalizeProfileEmail(userEmail);
  return email ? `${MARKET_PROFILE_STORAGE_KEY}:${email}` : MARKET_PROFILE_STORAGE_KEY;
}

function buildDemoGradingAnswers(): GradingAnswerSnapshot[] {
  return [
    {
      questionId: 9301,
      factorName: 'Самостоятельность в работе',
      questionText: 'Насколько самостоятельно вы ведете рабочие задачи?',
      answerText: 'Определяю приоритеты направления и отвечаю за итог перед руководством',
      answerScore: 4,
    },
    {
      questionId: 9302,
      factorName: 'Ответственность за сделки',
      questionText: 'Какова ваша роль в сделках или ключевых проектах?',
      answerText: 'Отвечаю за портфель сделок и распределяю их внутри команды',
      answerScore: 4,
    },
    {
      questionId: 9303,
      factorName: 'Влияние на инвестиционные решения',
      questionText: 'Как вы влияете на инвестиционные решения?',
      answerText: 'Участвую в принятии решений или имею право голоса',
      answerScore: 4,
    },
  ];
}

function buildDemoGradingResult(): GradingResultSnapshot {
  return {
    familyName: 'Инвестиции',
    specializationName: null,
    roleClusterName: 'Инвестиционная команда',
    totalScore: 17,
    roleName: 'Директор',
    gradeLevel: 4,
  };
}

function buildDemoPositionData(): PositionFormData {
  return finalizePositionFormForSubmit(normalizePositionFormData({
    industryName: 'Private Equity',
    sectorName: 'Прямые инвестиции',
    companyName: 'Zarya VC',
    companyRoleName: 'Директор инвестиций',
    baseGrossSalaryMonthly: '708333',
    baseGrossSalary: '8500000',
    salaryReviewFrequency: 'Раз в год',
    salaryReviewDate: 'Январь',
    stiTargetPct: '80',
    stiPayoutFrequency: 'Раз в год',
    stiPayoutMonth: 'Март',
    stiSizingBasis: 'kpi',
    stiCorporateKpiExamples: 'IRR портфеля, DPI фонда',
    stiIndividualKpiExamples: 'закрытые сделки, качество пайплайна',
    stiFormulaType: 'additive',
    stiCorporateKpiWeight: '40',
    stiIndividualKpiWeight: '60',
    ltipProgramType: 'real_carry',
    ltipProgramValueMode: 'percent',
    ltipProgramValuePct: '200',
    ltipProgramDuration: '5 лет',
    ltipVestingType: 'linear',
    ltipLastPayoutMode: 'absolute',
    ltipLastPayoutAmount: '4000000',
  }));
}

function buildDemoUserProfile(email: string, ts: string): UserProfileState {
  return {
    data: {
      lastName: 'Зайцев',
      firstName: 'Алексей',
      pseudonym: 'Северный мандат',
      email,
      sectorName: 'Прямые инвестиции',
      companyName: 'Zarya VC',
      positionName: 'Директор инвестиций',
      phone: '+7 926 555 12 34',
    },
    visibility: {
      pseudonymOnly: false,
      showPosition: true,
      showSector: true,
      showCompany: false,
    },
    updatedAt: ts,
  };
}

/** Сборка профиля для dev-пресета */
export function buildMarketProfileForDevPreset(
  email: string,
  preset: MarketProfileDevPreset,
): MarketProfileState {
  const ts = new Date().toISOString();

  if (preset === 'empty') {
    const empty = emptyMarketProfile();
    return {
      ...empty,
      userProfile: {
        ...empty.userProfile,
        data: { ...empty.userProfile.data, email },
      },
    };
  }

  const userProfile = buildDemoUserProfile(email, ts);
  const positionData = buildDemoPositionData();
  const gradingAnswers = buildDemoGradingAnswers();
  const gradingResult = buildDemoGradingResult();

  if (preset === 'pending_review') {
    return {
      userProfile,
      position: {
        status: 'pending_review',
        data: positionData,
        updatedAt: ts,
        reviewSubmittedAt: ts,
      },
      grading: {
        status: 'pending_review',
        result: gradingResult,
        answers: gradingAnswers,
        updatedAt: ts,
        reviewSubmittedAt: ts,
      },
      confirmedAt: null,
    };
  }

  return {
    userProfile,
    position: {
      status: 'complete',
      data: positionData,
      updatedAt: ts,
      reviewSubmittedAt: null,
      approvedAt: ts,
    },
    grading: {
      status: 'complete',
      result: gradingResult,
      answers: gradingAnswers,
      updatedAt: ts,
      reviewSubmittedAt: null,
      approvedAt: ts,
    },
    confirmedAt: ts,
  };
}

/** Демо-профиль для dev-администратора сообщества */
export function buildSeededMarketProfile(email: string): MarketProfileState {
  return buildMarketProfileForDevPreset(email, 'access_granted');
}

/** Применяет dev-пресет и сохраняет в localStorage */
export function applyDevMarketProfilePreset(
  userEmail: string,
  preset: MarketProfileDevPreset,
): MarketProfileState {
  setMarketProfileDevPresetStorage(preset);
  const next = buildMarketProfileForDevPreset(userEmail, preset);
  saveMarketProfile(next, userEmail);
  return next;
}

export function isMarketProfileReady(state: MarketProfileState): boolean {
  return computeProgress(state).completed === 2 && Boolean(state.confirmedAt);
}

/**
 * Заполняет профиль демо-данными для dev-админа, если рыночные данные ещё не открыты.
 * Возвращает сохранённое состояние или null, если сид не нужен.
 */
export function seedMarketProfileIfNeeded(userEmail: string | null | undefined): MarketProfileState | null {
  if (normalizeProfileEmail(userEmail) !== COMMUNITY_DEV_ADMIN_EMAIL) return null;
  if (getMarketProfileDevPreset() !== null) return null;
  const existing = loadMarketProfile(userEmail);
  if (isMarketProfileReady(existing)) return null;
  const seeded = buildSeededMarketProfile(userEmail!);
  saveMarketProfile(seeded, userEmail);
  return seeded;
}

export function loadMarketProfile(userEmail?: string | null): MarketProfileState {
  try {
    const key = marketProfileStorageKey(userEmail);
    let raw = localStorage.getItem(key);

    if (!raw && userEmail) {
      raw = localStorage.getItem(MARKET_PROFILE_STORAGE_KEY);
    }

    if (!raw) return emptyMarketProfile();
    const parsed = JSON.parse(raw) as Partial<MarketProfileState>;
    const empty = emptyMarketProfile();
    const merged: MarketProfileState = {
      userProfile: {
        data: {
          ...empty.userProfile.data,
          ...(parsed.userProfile?.data ?? {}),
        },
        visibility: {
          ...empty.userProfile.visibility,
          ...(parsed.userProfile?.visibility ?? {}),
        },
        updatedAt: parsed.userProfile?.updatedAt ?? null,
      },
      position: {
        status: parsed.position?.status ?? 'empty',
        data: normalizePositionFormData({ ...emptyPositionForm(), ...(parsed.position?.data ?? {}) }),
        updatedAt: parsed.position?.updatedAt ?? null,
        reviewSubmittedAt: parsed.position?.reviewSubmittedAt ?? null,
      },
      grading: {
        status: parsed.grading?.status ?? 'empty',
        result: parsed.grading?.result ?? null,
        answers: parsed.grading?.answers ?? [],
        updatedAt: parsed.grading?.updatedAt ?? null,
        reviewSubmittedAt: parsed.grading?.reviewSubmittedAt ?? null,
      },
      confirmedAt: parsed.confirmedAt ?? null,
    };
    return merged;
  } catch {
    return emptyMarketProfile();
  }
}

export function mergeRemoteMarketProfile(
  local: MarketProfileState,
  remote: Partial<MarketProfileState> | null | undefined,
): MarketProfileState {
  if (!remote) return local;
  return {
    userProfile: {
      data: { ...local.userProfile.data, ...(remote.userProfile?.data ?? {}) },
      visibility: { ...local.userProfile.visibility, ...(remote.userProfile?.visibility ?? {}) },
      updatedAt: remote.userProfile?.updatedAt ?? local.userProfile.updatedAt,
    },
    position: remote.position
      ? {
          status: remote.position.status ?? local.position.status,
          data: normalizePositionFormData({ ...local.position.data, ...(remote.position.data ?? {}) }),
          updatedAt: remote.position.updatedAt ?? local.position.updatedAt,
          reviewSubmittedAt: remote.position.reviewSubmittedAt ?? local.position.reviewSubmittedAt,
          approvedAt: remote.position.approvedAt ?? local.position.approvedAt ?? null,
        }
      : local.position,
    grading: remote.grading
      ? {
          status: remote.grading.status ?? local.grading.status,
          result: remote.grading.result ?? local.grading.result,
          answers: remote.grading.answers ?? local.grading.answers,
          updatedAt: remote.grading.updatedAt ?? local.grading.updatedAt,
          reviewSubmittedAt: remote.grading.reviewSubmittedAt ?? local.grading.reviewSubmittedAt,
          approvedAt: remote.grading.approvedAt ?? local.grading.approvedAt ?? null,
        }
      : local.grading,
    confirmedAt: remote.confirmedAt ?? local.confirmedAt,
  };
}

export function saveMarketProfile(state: MarketProfileState, userEmail?: string | null) {
  try {
    const key = marketProfileStorageKey(userEmail ?? state.userProfile.data.email);
    localStorage.setItem(key, JSON.stringify(state));
    void import('../../../services/adminCommunityService')
      .then(({ syncMarketProfileToServer }) => syncMarketProfileToServer(state))
      .catch(() => {});
  } catch {
    /* ignore */
  }
}

export type ProfileProgress = {
  completed: number;
  submitted: number;
  total: 2;
  unlocked: boolean;
  outdated: boolean;
};

export function computeProgress(state: MarketProfileState): ProfileProgress {
  const completed =
    (state.position.status === 'complete' ? 1 : 0)
    + (state.grading.status === 'complete' ? 1 : 0);
  const submitted =
    (isQuestionnaireSubmitted(state.position.status) ? 1 : 0)
    + (isQuestionnaireSubmitted(state.grading.status) ? 1 : 0);
  const unlocked = hasMarketDataAccess(state) && Boolean(state.confirmedAt);
  return {
    completed,
    submitted,
    total: 2,
    unlocked,
    outdated: unlocked && isProfileOutdated(state),
  };
}

export function isProfileOutdated(state: MarketProfileState): boolean {
  const date = state.position.data.actualDate;
  if (!date) return false;
  const ts = new Date(date).getTime();
  if (Number.isNaN(ts)) return false;
  return Date.now() - ts > OUTDATED_AFTER_DAYS * 24 * 60 * 60 * 1000;
}

/**
 * Стабильный псевдо-перцентиль 32…74 для UI-режима без рыночной выборки.
 * Детерминирован по введенным данным, чтобы не «прыгал» между рендерами.
 */
export function stablePercentile(state: MarketProfileState): number {
  const seed = [
    positionFixValue(state.position.data),
    state.position.data.companyRoleName,
    state.position.data.sectorName,
    state.grading.result?.totalScore ?? 0,
  ].join('|');
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) {
    h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return 32 + (h % 43);
}

/** Стабильный мок-размер группы сравнения */
export function stableSampleSize(state: MarketProfileState): number {
  const seed = `${state.position.data.sectorName}|${state.grading.result?.roleClusterName ?? ''}`;
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) {
    h = (h * 17 + seed.charCodeAt(i)) >>> 0;
  }
  return 14 + (h % 38);
}

export type CompensationPositionLabel = 'ниже рынка' | 'в рынке' | 'выше рынка';

export type ComparisonScope = 'industry' | 'sector';

export type ComponentMarketStatus =
  | 'ниже рынка'
  | 'в рынке'
  | 'выше рынка'
  | 'выше медианы'
  | 'недостаточно данных';

export function stableSampleSizeForScope(state: MarketProfileState, scope: ComparisonScope): number {
  const base = stableSampleSize(state);
  return scope === 'industry' ? base + 86 : base;
}

export function stablePercentileForScope(state: MarketProfileState, scope: ComparisonScope): number {
  const base = stablePercentile(state);
  const offset = scope === 'industry' ? 4 : -3;
  return Math.max(28, Math.min(76, base + offset));
}

function hashSeed(parts: string[]): number {
  const seed = parts.join('|');
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) {
    h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return h;
}

export function componentMarketStatus(
  state: MarketProfileState,
  component: 'fix' | 'sti' | 'ltip' | 'total',
  scope: ComparisonScope,
): ComponentMarketStatus {
  const { data } = state.position;
  if (component === 'ltip' && !positionHasLtip(data)) {
    return 'недостаточно данных';
  }
  if (component === 'sti' && !positionStiValue(data)) {
    return 'недостаточно данных';
  }
  if (component === 'fix' && !positionFixValue(data)) {
    return 'недостаточно данных';
  }
  const h = hashSeed([component, scope, positionFixValue(data), positionStiValue(data), data.sectorName]);
  const bucket = h % 5;
  if (bucket === 0) return 'ниже рынка';
  if (bucket === 1 || bucket === 2) return 'в рынке';
  if (bucket === 3) return 'выше медианы';
  return 'выше рынка';
}

export function compensationHeadline(percentile: number): string {
  if (percentile < 38) return 'Ваша компенсация ниже рыночного диапазона';
  if (percentile <= 58) return 'Ваша компенсация находится в рынке';
  if (percentile <= 68) return 'Ваша компенсация находится в рынке';
  return 'Ваша компенсация выше медианы рынка';
}

export function isInsufficientSample(size: number): boolean {
  return size < 20;
}

export function formatActualDate(date: string): string | null {
  if (!date) return null;
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' }).format(d);
}

export function rolesForGradingLadder(
  state: MarketProfileState,
  tree?: GradingTreePayload | null,
): { name: string; gradeLevel: number }[] {
  if (!tree?.roleClusters?.length) return [];
  const clusterName = state.grading.result?.roleClusterName ?? '';
  const cluster = tree.roleClusters.find((c) => c.name === clusterName) ?? tree.roleClusters[0];
  if (!cluster) return [];
  return tree.roles
    .filter((r) => r.role_cluster_id === cluster.id)
    .sort((a, b) => (a.grade_level ?? 0) - (b.grade_level ?? 0))
    .map((r) => ({ name: r.name, gradeLevel: r.grade_level ?? 0 }));
}

export function hasRoleMismatch(state: MarketProfileState): boolean {
  const formal = (state.position.data.companyRoleName || '').trim().toLowerCase();
  const graded = (state.grading.result?.roleName || '').trim().toLowerCase();
  if (!formal || !graded) return false;
  return formal !== graded && !formal.includes(graded) && !graded.includes(formal);
}

export function marketDataInsight(state: MarketProfileState, scope: ComparisonScope, sampleSize: number): string {
  const percentile = stablePercentileForScope(state, scope);
  if (isInsufficientSample(sampleSize)) {
    return 'Сравнение ограничено: в выбранной группе пока недостаточно профилей для высокой надёжности результата.';
  }
  if (percentile > 62) {
    return 'Ваш общий пакет находится выше медианы выбранной группы, при этом фиксированная часть может оставаться близкой к рынку.';
  }
  if (percentile < 42) {
    return 'По выбранной группе сравнения переменная часть компенсации может влиять на общий результат сильнее фиксированной базы.';
  }
  return 'Компенсационный пакет в целом соответствует рыночному диапазону выбранной группы сравнения.';
}

export function percentileToLabel(p: number): CompensationPositionLabel {
  if (p < 40) return 'ниже рынка';
  if (p <= 60) return 'в рынке';
  return 'выше рынка';
}

/** Как профиль отображается другим участникам сообщества */
export function resolveCommunityDisplay(profile: UserProfileState) {
  const author = profileToAuthorProfile(profile);
  return {
    pseudonym: author.pseudonym,
    position: author.roleLevel !== '—' ? author.roleLevel : null,
    sector: author.sector !== '—' ? author.sector : null,
    company: author.company ?? null,
  };
}

/** AuthorProfile для бейджа в сообществе по настройкам видимости */
export function profileToAuthorProfile(profile: UserProfileState): AuthorProfile {
  const { data, visibility } = profile;
  const pseudonym = data.pseudonym.trim() || data.firstName.trim() || 'Участник';

  if (visibility.pseudonymOnly) {
    return {
      pseudonym,
      industry: 'Инвестиционный рынок',
      sector: '—',
      roleLevel: '—',
    };
  }

  return {
    pseudonym,
    industry: 'Инвестиционный рынок',
    sector: visibility.showSector && data.sectorName.trim() ? data.sectorName.trim() : '—',
    roleLevel: visibility.showPosition && data.positionName.trim() ? data.positionName.trim() : '—',
    company: visibility.showCompany && data.companyName.trim() ? data.companyName.trim() : undefined,
  };
}

/**
 * Демо-дерево грейдинга на случай, если API `/admin/grading/tree`
 * недоступен обычному участнику. Структура идентична модели проекта.
 */
export function buildDemoGradingTree(): GradingTreePayload {
  const ts = new Date().toISOString();
  const familyId = 9001;
  const rcInvest = 9101;
  const rcSupport = 9102;

  const factors = [
    { id: 9201, name: 'Самостоятельность в работе', description: 'Степень автономности при выполнении задач', question_hint: null },
    { id: 9202, name: 'Ответственность за сделки', description: 'Роль в сделках и проектах', question_hint: null },
    { id: 9203, name: 'Влияние на инвестиционные решения', description: 'Участие в принятии решений', question_hint: null },
    { id: 9204, name: 'Управление командой', description: 'Фактическое руководство людьми', question_hint: null },
    { id: 9205, name: 'Взаимодействие с руководством', description: 'Работа с партнерами и инвесткомитетом', question_hint: null },
  ].map((f) => ({ ...f, family_id: familyId, created_at: ts, updated_at: ts }));

  const mkAnswers = (questionId: number, texts: [string, string, string, string]) =>
    texts.map((answer_text, i) => ({
      id: questionId * 10 + i,
      question_id: questionId,
      name: null,
      answer_text,
      matrix_description: null,
      answer_score: i + 1,
      created_at: ts,
    }));

  const questions = [
    {
      id: 9301,
      diff_factor_id: 9201,
      question_text: 'Насколько самостоятельно вы ведете рабочие задачи?',
      answers: mkAnswers(9301, [
        'Работаю по детальным инструкциям, результат проверяется на каждом шаге',
        'Самостоятельно веду задачи в рамках согласованного подхода',
        'Сам определяю подход и контролирую результат по своему направлению',
        'Определяю приоритеты направления и отвечаю за итог перед руководством',
      ]),
    },
    {
      id: 9302,
      diff_factor_id: 9202,
      question_text: 'Какова ваша роль в сделках или ключевых проектах?',
      answers: mkAnswers(9302, [
        'Готовлю материалы и аналитику для команды сделки',
        'Веду отдельные рабочие блоки сделки под контролем старших коллег',
        'Веду сделку целиком как ответственный исполнитель',
        'Отвечаю за портфель сделок и распределяю их внутри команды',
      ]),
    },
    {
      id: 9303,
      diff_factor_id: 9203,
      question_text: 'Как вы влияете на инвестиционные решения?',
      answers: mkAnswers(9303, [
        'Не участвую в обсуждении решений',
        'Готовлю материалы, на основе которых принимаются решения',
        'Мое мнение учитывается при принятии решений',
        'Участвую в принятии решений или имею право голоса',
      ]),
    },
    {
      id: 9304,
      diff_factor_id: 9204,
      question_text: 'Управляете ли вы командой?',
      answers: mkAnswers(9304, [
        'Нет, работаю индивидуально',
        'Координирую работу младших коллег по задачам',
        'У меня в подчинении небольшая команда (1–3 человека)',
        'Руковожу командой или несколькими командами',
      ]),
    },
    {
      id: 9305,
      diff_factor_id: 9205,
      question_text: 'Как устроено ваше взаимодействие с руководством и инвесткомитетом?',
      answers: mkAnswers(9305, [
        'Взаимодействую только со своим руководителем',
        'Периодически представляю результаты руководству',
        'Регулярно защищаю материалы перед руководством или комитетом',
        'Напрямую работаю с партнерами / инвесткомитетом на постоянной основе',
      ]),
    },
  ];

  return {
    families: [{ id: familyId, name: 'Инвестиции (демо)', created_at: ts, updated_at: ts }],
    roleClusters: [
      { id: rcInvest, family_id: familyId, name: 'Инвестиционная команда', created_at: ts, updated_at: ts },
      { id: rcSupport, family_id: familyId, name: 'Поддержка инвестиций', created_at: ts, updated_at: ts },
    ],
    specializations: [],
    roles: [
      { id: 9401, role_cluster_id: rcInvest, specialization_id: null, name: 'Аналитик', description: null, grade_level: 1, min_score: 5, max_score: 8, created_at: ts, updated_at: ts },
      { id: 9402, role_cluster_id: rcInvest, specialization_id: null, name: 'Ассоциат', description: null, grade_level: 2, min_score: 9, max_score: 12, created_at: ts, updated_at: ts },
      { id: 9403, role_cluster_id: rcInvest, specialization_id: null, name: 'Вице-президент', description: null, grade_level: 3, min_score: 13, max_score: 16, created_at: ts, updated_at: ts },
      { id: 9404, role_cluster_id: rcInvest, specialization_id: null, name: 'Директор', description: null, grade_level: 4, min_score: 17, max_score: 20, created_at: ts, updated_at: ts },
      { id: 9405, role_cluster_id: rcSupport, specialization_id: null, name: 'Специалист', description: null, grade_level: 1, min_score: 5, max_score: 12, created_at: ts, updated_at: ts },
      { id: 9406, role_cluster_id: rcSupport, specialization_id: null, name: 'Руководитель направления', description: null, grade_level: 2, min_score: 13, max_score: 20, created_at: ts, updated_at: ts },
    ],
    factors,
    questions: questions.map(({ answers: _a, ...q }) => ({
      ...q,
      role_cluster_id: rcInvest,
      name: null,
      created_at: ts,
    })).concat(
      questions.map(({ answers: _a, ...q }) => ({
        ...q,
        id: q.id + 100,
        role_cluster_id: rcSupport,
        name: null,
        created_at: ts,
      })),
    ),
    answers: questions.flatMap((q) => q.answers).concat(
      questions.flatMap((q) => q.answers.map((a) => ({
        ...a,
        id: a.id + 1000,
        question_id: a.question_id + 100,
      }))),
    ),
    calibrationQuestions: [
      { id: 9501, family_id: familyId, question_text: 'Что ближе всего описывает вашу основную зону работы?', created_at: ts, updated_at: ts },
    ],
    calibrationAnswers: [
      { id: 9601, calibration_question_id: 9501, answer_text: 'Поиск, анализ и сопровождение сделок / инвестиций', target_role_cluster_id: rcInvest, sort_order: 1, created_at: ts },
      { id: 9602, calibration_question_id: 9501, answer_text: 'Поддерживающие функции: IR, юридическая, операционная работа', target_role_cluster_id: rcSupport, sort_order: 2, created_at: ts },
    ],
  };
}
